/**
 * Field collection + per-control UX: char counters, file-pill proxy button +
 * client-side type/size pre-check, and the link repeater (clone up to 5 rows).
 */

import type { IewConfig, ValuesMap } from './types';
import type { Enhancer } from './enhance';

export interface CollectedStep {
  values: ValuesMap;
  filesPresent: string[];
}

export class Fields {
  constructor(
    private readonly root: HTMLElement,
    private readonly config: IewConfig,
    private readonly enhancer: Enhancer,
  ) {}

  /** Wire counters, file pills and repeaters once, at startup. */
  init(): void {
    this.initCounters();
    this.initFilePills();
    this.initRepeaters();
  }

  /**
   * Gather the values of one step's fields, keyed by field_key. Multi-selects
   * and repeaters yield arrays; radios yield the checked value; the phone is
   * written back as E.164 before collection.
   */
  collectStep(step: number): CollectedStep {
    const panel = this.root.querySelector<HTMLElement>(`.iew-step[data-step="${step}"]`);
    const values: ValuesMap = {};
    const filesPresent: string[] = [];
    if (!panel) {
      return { values, filesPresent };
    }

    panel.querySelectorAll<HTMLElement>('.iew-field[data-field]').forEach((field) => {
      const key = field.dataset.field;
      if (!key) {
        return;
      }

      // Phone field: intl-tel-input wraps the input in a .iti container and may
      // inject its own inputs, so read the phone explicitly by class (a generic
      // querySelector('input') can hit ITI's search/hidden input instead) and
      // write the E.164 value back so the display reformats consistently.
      const phone = field.querySelector<HTMLInputElement>('input.iew-phone');
      if (phone) {
        const number = this.enhancer.phoneNumber();
        phone.value = number;
        values[key] = number;
        return;
      }

      // File fields: presence only (the file itself rides the FormData submit).
      const file = field.querySelector<HTMLInputElement>('input[type="file"]');
      if (file) {
        if (file.files && file.files.length > 0) {
          filesPresent.push(key);
        }
        return;
      }

      const multi = field.querySelector<HTMLSelectElement>('select[multiple]');
      if (multi) {
        values[key] = Array.from(multi.selectedOptions).map((o) => o.value);
        return;
      }

      const single = field.querySelector<HTMLSelectElement>('select:not([multiple])');
      if (single) {
        values[key] = single.value;
        return;
      }

      const radios = field.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      if (radios.length > 0) {
        const checked = Array.from(radios).find((r) => r.checked);
        values[key] = checked ? checked.value : '';
        return;
      }

      const repeater = field.querySelector<HTMLElement>('.iew-repeater');
      if (repeater) {
        const rows = repeater.querySelectorAll<HTMLInputElement>('.iew-repeater__row input');
        values[key] = Array.from(rows).map((i) => i.value.trim()).filter((v) => v !== '');
        return;
      }

      const textarea = field.querySelector<HTMLTextAreaElement>('textarea');
      if (textarea) {
        values[key] = textarea.value;
        return;
      }

      const input = field.querySelector<HTMLInputElement>('input');
      if (input) {
        values[key] = input.value;
      }
    });

    return { values, filesPresent };
  }

  private initCounters(): void {
    this.root.querySelectorAll<HTMLTextAreaElement>('textarea[maxlength]').forEach((ta) => {
      const counter = ta.parentElement?.querySelector<HTMLElement>('.iew-field__count');
      const max = ta.maxLength;
      const update = (): void => {
        if (counter) {
          counter.textContent = `${ta.value.length}/${max}`;
        }
      };
      ta.addEventListener('input', update);
      update();
    });
  }

  private initFilePills(): void {
    this.root.querySelectorAll<HTMLElement>('.iew-file').forEach((pill) => {
      const key = pill.dataset.file ?? '';
      const input = pill.querySelector<HTMLInputElement>('input[type="file"]');
      const nameEl = pill.querySelector<HTMLElement>('.iew-file__name');
      const pick = pill.querySelector<HTMLButtonElement>('.iew-file__pick');
      const field = pill.closest<HTMLElement>('.iew-field');
      if (!input || !nameEl || !pick) {
        return;
      }

      pick.addEventListener('click', () => input.click());

      input.addEventListener('change', () => {
        const errEl = field?.querySelector<HTMLElement>('.iew-field__error');
        const clearErr = (): void => {
          field?.classList.remove('has-error');
          if (errEl) {
            errEl.hidden = true;
            errEl.textContent = '';
          }
        };
        const showErr = (msg: string): void => {
          input.value = '';
          nameEl.textContent = this.config.i18n.noFileSelected ?? '';
          field?.classList.add('has-error');
          if (errEl) {
            errEl.textContent = msg;
            errEl.hidden = false;
          }
        };

        const file = input.files && input.files[0];
        if (!file) {
          nameEl.textContent = this.config.i18n.noFileSelected ?? '';
          clearErr();
          return;
        }

        const max = this.config.maxFileBytes[key] ?? 0;
        if (max > 0 && file.size > max) {
          showErr(this.config.i18n.fileTooLarge ?? '');
          return;
        }
        if (!this.acceptOk(input, file)) {
          showErr(this.config.i18n.fileType ?? '');
          return;
        }

        clearErr();
        nameEl.textContent = file.name;
      });
    });
  }

  private acceptOk(input: HTMLInputElement, file: File): boolean {
    const accept = input.accept.trim();
    if (accept === '') {
      return true;
    }
    const allowed = accept.split(',').map((s) => s.trim().toLowerCase());
    return allowed.includes(file.type.toLowerCase());
  }

  private initRepeaters(): void {
    this.root.querySelectorAll<HTMLElement>('.iew-repeater').forEach((rep) => {
      const max = rep.dataset.max ? Number(rep.dataset.max) : 5;
      const tpl = rep.querySelector<HTMLTemplateElement>('.iew-repeater__template');
      const add = rep.querySelector<HTMLButtonElement>('.iew-repeater__add');

      this.bindRepeaterRows(rep);

      add?.addEventListener('click', () => {
        if (!tpl || this.repeaterRows(rep).length >= max) {
          return;
        }
        const frag = tpl.content.cloneNode(true) as DocumentFragment;
        const row = frag.querySelector<HTMLElement>('.iew-repeater__row');
        if (row) {
          add.insertAdjacentElement('beforebegin', row);
          this.bindRepeaterRows(rep);
        }
      });
    });
  }

  /** Live rows only — <template> content is inert and not matched here. */
  private repeaterRows(rep: HTMLElement): HTMLElement[] {
    return Array.from(rep.querySelectorAll<HTMLElement>('.iew-repeater__row'));
  }

  /**
   * Bind remove handlers (once per row) and refresh × visibility for one
   * repeater. Idempotent, so it is safe to call again after a draft restore
   * injects extra rows. The last remaining row has nothing meaningful to remove
   * (the field is optional), so its × is hidden — a lone empty row with a dead
   * button reads as broken; the button reappears as soon as a second row exists.
   */
  private bindRepeaterRows(rep: HTMLElement): void {
    const current = this.repeaterRows(rep);
    current.forEach((row) => {
      const btn = row.querySelector<HTMLButtonElement>('.iew-repeater__remove');
      if (!btn) {
        return;
      }
      if (btn.dataset.iewBound !== '1') {
        btn.dataset.iewBound = '1';
        btn.addEventListener('click', () => {
          // × is hidden on the last row, so a click always means 2+ rows.
          row.remove();
          this.bindRepeaterRows(rep);
        });
      }
      btn.hidden = current.length <= 1;
    });
  }

  /** Re-sync repeater rows after an external mutation (e.g. draft restore). */
  syncRepeaters(): void {
    this.root.querySelectorAll<HTMLElement>('.iew-repeater').forEach((rep) => {
      this.bindRepeaterRows(rep);
    });
  }
}
